import React, { useEffect, useMemo, useState, useRef } from "react";
import {
    Button,
    Card,
    Col,
    Form,
    Modal,
    Row,
    Table,
    Badge,
    Dropdown,
} from "react-bootstrap";
import WhatsAppTemplateService from "../../services/WhatsAppTemplateService";
import {
    WhatsAppTemplateCategory,
    WhatsAppTemplateStatus,
    WhatsAppTemplateHeaderType,
} from "../../constants/whatsAppConstants";
import { API_STATUS } from '../../constants/apiStatusCode';
import { FiEdit2 } from "react-icons/fi";
import { GrView } from "react-icons/gr";
import { RiDeleteBinLine } from "react-icons/ri";
import { FaPhoneAlt, FaExternalLinkAlt } from "react-icons/fa";


const PAGE_SIZE = 5;
const API_THROTTLE_TIME = 5 * 60 * 1000; // 5 minutes in milliseconds
const MIN_SEARCH_LENGTH = 3;

const getSearchQuery = (value) => {
    const trimmed = (value || "").trim();
    if (!trimmed || trimmed.length < MIN_SEARCH_LENGTH) return undefined;
    return trimmed;
};

const isSearchTooShort = (value) => {
    const trimmed = (value || "").trim();
    return trimmed.length > 0 && trimmed.length < MIN_SEARCH_LENGTH;
};

// Persist throttle timestamps, paging info and cached data across component
// unmounts (e.g. when switching WhatsApp tabs).
let lastTemplatesCallTs = 0;
let lastTemplatesPageGlobal = 0;

let cachedTemplates = [];
let cachedTotalCount = 0;
let cachedPage = 1;
let cachedSearch = "";
let cachedStatusFilter = "ALL";

// Detect full page reload once per JS bundle load; survives WhatsApp tab switches
const isFullPageReload = () => {
    try {
        const navEntry = performance.getEntriesByType("navigation")[0];
        return navEntry?.type === "reload";
    } catch {
        return false;
    }
};

let pendingAutoSyncFromMeta = isFullPageReload();
let hasAutoSyncedFromMetaThisSession = false;

// Map numeric status -> label + badge color
const STATUS_LABELS = {
    [WhatsAppTemplateStatus.APPROVED]: "APPROVED",
    [WhatsAppTemplateStatus.PENDING]: "PENDING",
    [WhatsAppTemplateStatus.REJECTED]: "REJECTED",
    [WhatsAppTemplateStatus.PAUSED]: "PAUSED",
    [WhatsAppTemplateStatus.DISABLED]: "DISABLED",
};

const STATUS_COLORS = {
    [WhatsAppTemplateStatus.APPROVED]: "success",
    [WhatsAppTemplateStatus.PENDING]: "warning",
    [WhatsAppTemplateStatus.REJECTED]: "danger",
    [WhatsAppTemplateStatus.PAUSED]: "secondary",
    [WhatsAppTemplateStatus.DISABLED]: "secondary",
};

// Map numeric category -> label
const CATEGORY_LABELS = {
    [WhatsAppTemplateCategory.MARKETING]: "MARKETING",
    [WhatsAppTemplateCategory.UTILITY]: "UTILITY",
    [WhatsAppTemplateCategory.AUTHENTICATION]: "AUTHENTICATION",
};

// Build options as { key: 'MARKETING', value: 1 } etc.
const CATEGORY_OPTIONS = Object.entries(WhatsAppTemplateCategory).map(
    ([key, value]) => ({ key, value })
);

const LANGUAGE_OPTIONS = ["en_US", "en",  "hi", "es"];

const BUTTON_TYPES = {
    VISIT_WEBSITE: "VISIT_WEBSITE",
    CALL_WHATSAPP: "CALL_WHATSAPP",
};

const PAYLOAD_BUTTON_TYPES = {
    URL: "URL",
    PHONE_NUMBER: "PHONE_NUMBER",
};

const isValidHttpUrl = (value) => {
    try {
        const u = new URL(value);
        return u.protocol === "http:" || u.protocol === "https:";
    } catch {
        return false;
    }
};

const WhatsAppTemplate = () => {
    // Initialise state from cached values so that when we throttle API calls
    // after tab changes, the previously loaded data is still shown.
    const [templates, setTemplates] = useState(() => cachedTemplates);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(() => cachedPage || 1);
    const [totalCount, setTotalCount] = useState(() => cachedTotalCount);
    const [search, setSearch] = useState(() => cachedSearch);
    const [statusFilter, setStatusFilter] = useState(() => cachedStatusFilter || "ALL");

    const [showEditModal, setShowEditModal] = useState(false);
    const [editMode, setEditMode] = useState("create"); // 'create' | 'edit'
    const [currentTemplate, setCurrentTemplate] = useState(null);

    const [showViewModal, setShowViewModal] = useState(false);
    const [viewTemplate, setViewTemplate] = useState(null);

    // Throttling refs to track last API call times and last page.
    // Initialise from module-level variables so values persist across tab changes
    // (component unmounts / remounts).
    const lastTemplatesCall = useRef(lastTemplatesCallTs);
    const lastTemplatesPage = useRef(lastTemplatesPageGlobal);

    const totalPages = useMemo(
        () => (totalCount ? Math.ceil(totalCount / PAGE_SIZE) : 1),
        [totalCount]
    );

    useEffect(() => {
        fetchTemplates();
    }, [page, statusFilter]);

    // Cleanup preview URL when component unmounts or template changes
    useEffect(() => {
        return () => {
            if (currentTemplate?.headerImagePreview) {
                URL.revokeObjectURL(currentTemplate.headerImagePreview);
            }
        };
    }, [currentTemplate?.headerImagePreview]);


    const fetchTemplates = async (force = false) => {
        const now = Date.now();
        // Only throttle if it's the same page and within throttle time
        const isSamePage = lastTemplatesPage.current === page;
        const isSameSearch = search === cachedSearch;
        const isSameStatus = statusFilter === cachedStatusFilter;
        if (!force && isSamePage && isSameSearch && isSameStatus && (now - lastTemplatesCall.current) < API_THROTTLE_TIME) {
            console.log("Templates API call throttled - last call was less than 5 minutes ago");
            return;
        }

        try {
            setLoading(true);
            lastTemplatesCall.current = now;
            lastTemplatesPage.current = page;
            lastTemplatesCallTs = now;
            lastTemplatesPageGlobal = page;
            const params = {
                PageNumber: page,
                PageSize: PAGE_SIZE,
                Search: getSearchQuery(search),
                Status: statusFilter === "ALL"
                    ? 0
                    : Number(statusFilter),
            };

            const resp = await WhatsAppTemplateService.getAllWhatsAppTemplates(
                params
            );

            const data = resp?.data?.data || resp?.data || resp?.["data"];

            const items = Array.isArray(data?.items) ? data.items : [];
            const mappedTemplates =
                items.map((t) => ({
                    id: t.id,
                    metaTemplateId: t.metaTemplateId,
                    name: t.name,
                    status: t.status,
                    category: t.category,
                    language: t.language,
                    description: t.description,
                    header: t.headerText,
                    body: t.bodyText,
                    footer: t.footerText,
                    headerUrl: t.headerUrl,
                    headerType: t.headerType,
                    buttons: t.buttonsComponent
                        ? (t.buttonsComponent.buttons || []).map((btn, idx) => {
                            if (btn.type === PAYLOAD_BUTTON_TYPES.URL) {
                                return {
                                    id: Date.now() + idx,
                                    type: BUTTON_TYPES.VISIT_WEBSITE,
                                    text: btn.text,
                                    url: btn.url,
                                };
                            }
                            if (btn.type === PAYLOAD_BUTTON_TYPES.PHONE_NUMBER) {
                                return {
                                    id: Date.now() + idx,
                                    type: BUTTON_TYPES.CALL_WHATSAPP,
                                    text: btn.text,
                                    phone_number: btn.phone_number,
                                };
                            }
                            return null;
                        }).filter(Boolean)
                        : [],
                }));
            setTemplates(mappedTemplates);
            setTotalCount(data?.totalCount || items.length);

            // Update cache so data is preserved across unmounts
            cachedTemplates = mappedTemplates;
            cachedTotalCount = data?.totalCount || items.length;
            cachedPage = page;
            cachedSearch = search;
            cachedStatusFilter = statusFilter;
        } catch (e) {
            console.error("Failed to load WhatsApp templates", e);
        } finally {
            setLoading(false);
        }
    };

    const handleSearchChange = (e) => {
        setSearch(e.target.value);
        setPage(1);
    };

    const handleSearchSubmit = (e) => {
        e.preventDefault();
        if (isSearchTooShort(search)) return;
        fetchTemplates(true);
    };

    const syncFromMeta = async ({ silent = false } = {}) => {
        try {
            const response = await WhatsAppTemplateService.syncFromMeta();
            if (response.data.statusCode === API_STATUS.SUCCESS) {
                if (!silent) {
                    alert("Templates synced successfully");
                }
                await fetchTemplates(true); // Force refresh after sync
            } else if (!silent) {
                alert(response?.data?.message || "Failed to sync templates");
            } else {
                console.warn(
                    "Auto sync from Meta:",
                    response?.data?.message || "Failed to sync templates",
                );
            }
        } catch (e) {
            console.error("Failed to sync templates", e);
            if (!silent) {
                alert("Failed to sync templates");
            }
        }
    };

    // Auto-sync once after a full page refresh (not when switching WhatsApp tabs)
    useEffect(() => {
        if (!pendingAutoSyncFromMeta || hasAutoSyncedFromMetaThisSession) {
            return;
        }
        hasAutoSyncedFromMetaThisSession = true;
        pendingAutoSyncFromMeta = false;
        syncFromMeta({ silent: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const openCreateModal = () => {
        setEditMode("create");
        setCurrentTemplate({
            id: null,
            name: "",
            category: WhatsAppTemplateCategory.MARKETING, // numeric default
            language: "en_US",
            description: "",
            headerType: WhatsAppTemplateHeaderType.TEXT,
            header: "",
            headerImageFileName: "",
            headerImageFile: null,
            headerImagePreview: null,
            body: "",
            footer: "",
            buttons: [],
        });
        setShowEditModal(true);
    };

    const openEditModal = (tpl) => {
        setEditMode("edit");
        setCurrentTemplate({
            headerType: tpl.headerType || WhatsAppTemplateHeaderType.TEXT,
            headerImageFileName: tpl.headerImageFileName || "",
            headerImageFile: null,
            headerImagePreview: null,
            buttons: tpl.buttons || [],
            ...tpl,
        });
        setShowEditModal(true);
    };

    const openViewModal = (tpl) => {
        setViewTemplate(tpl);
        setShowViewModal(true);
    };

    const handleDelete = async (tpl) => {
        if (!window.confirm(`Delete template "${tpl.name}"?`)) return;
        try {
            await WhatsAppTemplateService.deleteWhatsAppTemplate(tpl.metaTemplateId);
            await fetchTemplates(true); // Force refresh after delete
        } catch (e) {
            console.error("Failed to delete WhatsApp template", e);
            alert("Failed to delete WhatsApp template");
        }
    };

    const validateTemplateForSubmit = (tpl) => {
        if (!tpl) return "Template details are missing.";

        if (!String(tpl.name || "").trim()) {
            return "Template Name is required.";
        }
        if (!String(tpl.description || "").trim()) {
            return "Description is required.";
        }
        if (!String(tpl.body || "").trim()) {
            return "Body is required.";
        }
        if (!tpl.category) {
            return "Category is required.";
        }
        if (!String(tpl.language || "").trim()) {
            return "Language is required.";
        }

        if (tpl.headerType === WhatsAppTemplateHeaderType.IMAGE) {
            const hasHeaderImage =
                String(tpl.headerImageFileName || "").trim() ||
                String(tpl.headerUrl || "").trim();
            if (!hasHeaderImage) {
                return "Header image is required when header type is Image.";
            }
        }

        const buttons = tpl.buttons || [];
        for (let i = 0; i < buttons.length; i += 1) {
            const btn = buttons[i];
            const index = i + 1;
            const text = String(btn?.text || "").trim();
            if (!text) {
                return `Button ${index}: text is required.`;
            }

            if (btn.type === BUTTON_TYPES.VISIT_WEBSITE) {
                const url = String(btn?.url || "").trim();
                if (!url) {
                    return `Button ${index}: Website URL is required.`;
                }
                if (!isValidHttpUrl(url)) {
                    return `Button ${index}: Website URL must start with http:// or https://`;
                }
            }

            if (btn.type === BUTTON_TYPES.CALL_WHATSAPP) {
                const phone = String(btn?.phone_number || "").trim();
                if (!phone) {
                    return `Button ${index}: Phone number is required.`;
                }
            }
        }

        return null;
    };

    const handleSave = async () => {
        if (!currentTemplate) return;
        const validationError = validateTemplateForSubmit(currentTemplate);
        if (validationError) {
            alert(validationError);
            return;
        }

        try {
            const buttonsComponent =
                (currentTemplate.buttons || []).length > 0
                    ? {
                        type: "BUTTONS",
                        buttons: (currentTemplate.buttons || [])
                            .map((btn) => {
                                if (btn.type === BUTTON_TYPES.VISIT_WEBSITE) {
                                    return {
                                        type: PAYLOAD_BUTTON_TYPES.URL,
                                        text: btn.text,
                                        url: btn.url,
                                    };
                                }
                                if (btn.type === BUTTON_TYPES.CALL_WHATSAPP) {
                                    return {
                                        type: PAYLOAD_BUTTON_TYPES.PHONE_NUMBER,
                                        text: btn.text,
                                        phone_number: btn.phone_number,
                                    };
                                }
                                return null;
                            })
                            .filter(Boolean),
                    }
                    : null;

            const payload = {
                metaTemplateId: "",
                name: currentTemplate.name,
                category: currentTemplate.category,
                language: currentTemplate.language,
                description: currentTemplate.description,
                headerType: currentTemplate.headerType,
                headerText:
                    currentTemplate.headerType === WhatsAppTemplateHeaderType.TEXT
                        ? currentTemplate.header
                        : null,
                headerUrl:
                    currentTemplate.headerType === WhatsAppTemplateHeaderType.IMAGE
                        ? currentTemplate.headerImageFileName //"1235407872035752"
                        : null,
                bodyText: currentTemplate.body,
                footerText: currentTemplate.footer,
                status: WhatsAppTemplateStatus.PENDING,
                rejectionReason: null,
                approvedOn: null,
                lastSyncedOn: null,
                ...(buttonsComponent ? { buttonsComponent } : {}),
            };

            var res = await WhatsAppTemplateService.saveWhatsAppTemplate(payload);
            if (
                res.data.statusCode === API_STATUS.SUCCESS ||
                res.data.statusCode === API_STATUS.CREATED
            ) {
                setShowEditModal(false);
                await fetchTemplates(true); // Force refresh after save
                alert(res?.data?.message);
            } else {
                alert(res?.data?.message || "Failed to save template");
            }
        } catch (e) {
            alert("Failed to save WhatsApp template");
            console.error("Failed to save WhatsApp template", e);
        }
    };

    const handleEdit = async () => {
        if (!currentTemplate) return;
        const validationError = validateTemplateForSubmit(currentTemplate);
        if (validationError) {
            alert(validationError);
            return;
        }
        try {
            const buttonsComponent =
                (currentTemplate.buttons || []).length > 0
                    ? {
                        type: "BUTTONS",
                        buttons: (currentTemplate.buttons || [])
                            .map((btn) => {
                                if (btn.type === BUTTON_TYPES.VISIT_WEBSITE) {
                                    return {
                                        type: PAYLOAD_BUTTON_TYPES.URL,
                                        text: btn.text,
                                        url: btn.url,
                                    };
                                }
                                if (btn.type === BUTTON_TYPES.CALL_WHATSAPP) {
                                    return {
                                        type: PAYLOAD_BUTTON_TYPES.PHONE_NUMBER,
                                        text: btn.text,
                                        phone_number: btn.phone_number,
                                    };
                                }
                                return null;
                            })
                            .filter(Boolean),
                    }
                    : null;

            const payload = {
                metaTemplateId: currentTemplate.metaTemplateId,
                name: currentTemplate.name,
                category: currentTemplate.category,
                language: currentTemplate.language,
                description: currentTemplate.description,
                headerType: currentTemplate.headerType,
                headerText:
                    currentTemplate.headerType === WhatsAppTemplateHeaderType.TEXT
                        ? currentTemplate.header
                        : null,
                headerUrl:
                    currentTemplate.headerType === WhatsAppTemplateHeaderType.IMAGE
                        ? currentTemplate.headerImageFileName //"1235407872035752"
                        : null,
                bodyText: currentTemplate.body,
                footerText: currentTemplate.footer,
                status: WhatsAppTemplateStatus.PENDING,
                rejectionReason: null,
                approvedOn: null,
                lastSyncedOn: null,
                ...(buttonsComponent ? { buttonsComponent } : {}),
            };

            var res = await WhatsAppTemplateService.editWhatsAppTemplate(payload);
            if (
                res.data.statusCode === API_STATUS.SUCCESS ||
                res.data.statusCode === API_STATUS.CREATED
            ) {
                setShowEditModal(false);
                await fetchTemplates(true); // Force refresh after edit
                alert(res?.data?.message);
            } else {
                alert(res?.data?.message || "Failed to edit template");
            }

        } catch (e) {
            alert("Failed to edit WhatsApp template");
            console.error("Failed to edit WhatsApp template", e);
        }
    };

    const addButton = (type) => {
        setCurrentTemplate((prev) => {
            const existing = prev.buttons || [];
            // Limit to 5 buttons
            if (existing.length >= 5) return prev;

            const baseText =
                type === BUTTON_TYPES.VISIT_WEBSITE ? "Visit Website" : "Call on WhatsApp";

            const makeUniqueText = (text) => {
                const lowerSet = new Set(
                    (existing || []).map((b) => (b?.text || "").trim().toLowerCase())
                );
                if (!lowerSet.has(text.trim().toLowerCase())) return text;
                let i = 2;
                while (lowerSet.has(`${text} ${i}`.trim().toLowerCase())) i += 1;
                return `${text} ${i}`;
            };

            const uniqueText = makeUniqueText(baseText);

            const newButton =
                type === BUTTON_TYPES.VISIT_WEBSITE
                    ? {
                        id: Date.now(),
                        type,
                        text: uniqueText,
                        url: "",
                    }
                    : {
                        id: Date.now(),
                        type,
                        text: uniqueText,
                        phone_number: "",
                    };

            return {
                ...prev,
                buttons: [...existing, newButton],
            };
        });
    };

    const updateButton = (id, changes) => {
        setCurrentTemplate((prev) => {
            const nextButtons = (prev.buttons || []).map((btn) =>
                btn.id === id ? { ...btn, ...changes } : btn
            );

            // Enforce unique button text (case-insensitive)
            if (Object.prototype.hasOwnProperty.call(changes, "text")) {
                const nextText = (changes.text || "").trim().toLowerCase();
                if (nextText) {
                    const duplicate = nextButtons.some(
                        (b) => b.id !== id && (b.text || "").trim().toLowerCase() === nextText
                    );
                    if (duplicate) {
                        alert("Button names must be unique.");
                        return prev; // reject change
                    }
                }
            }

            return {
                ...prev,
                buttons: nextButtons,
            };
        });
    };

    const removeButton = (id) => {
        setCurrentTemplate((prev) => ({
            ...prev,
            buttons: (prev.buttons || []).filter((btn) => btn.id !== id),
        }));
    };

    const renderStatusBadge = (status) => {
        const variant = STATUS_COLORS[status] || "secondary";
        const label = STATUS_LABELS[status] || "UNKNOWN";
        return (
            <Badge bg={variant} pill>
                {label}
            </Badge>
        );
    };

    const renderCategoryLabel = (category) => {
        return CATEGORY_LABELS[category] || "UNKNOWN";
    };

    return (
        <div className="py-3">
            <Row className="mb-3">
                <Col>
                    <h3 className="fw-bold">Templates Management</h3>
                    <div className="text-muted">
                        Create and manage WhatsApp message templates. Templates must be
                        approved by Meta before use.
                    </div>
                </Col>
                <Col className="text-end d-flex justify-content-end align-items-start gap-2">
                    <Button variant="outline-secondary" onClick={() => syncFromMeta()}>Sync from Meta</Button>
                    <Button variant="primary" onClick={openCreateModal}>
                        + Create Template
                    </Button>
                </Col>
            </Row>

            <Card>
                <Card.Body>
                    <Form onSubmit={handleSearchSubmit} className="d-flex flex-column gap-1 mb-3">
                        <div className="d-flex gap-3">
                        <Form.Control
                            type="text"
                            title="Press Enter to search"
                            placeholder="Search templates (min 3 characters)..."
                            value={search}
                            onChange={handleSearchChange}
                        />
                        <Form.Select
                            style={{ maxWidth: 180 }}
                            value={statusFilter}
                            onChange={(e) => {
                                setStatusFilter(e.target.value);
                                setPage(1);
                            }}
                        >
                            <option value="ALL">All Status</option>
                            <option value={WhatsAppTemplateStatus.APPROVED}>Approved</option>
                            <option value={WhatsAppTemplateStatus.PENDING}>Pending</option>
                            <option value={WhatsAppTemplateStatus.REJECTED}>Rejected</option>
                            <option value={WhatsAppTemplateStatus.PAUSED}>Paused</option>
                            <option value={WhatsAppTemplateStatus.DISABLED}>Disabled</option>
                        </Form.Select>
                        </div>
                        {isSearchTooShort(search) ? (
                            <Form.Text className="text-danger">
                                Enter at least {MIN_SEARCH_LENGTH} characters to search.
                            </Form.Text>
                        ) : null}
                    </Form>

                    <div className="table-responsive">
                        <Table hover className="align-middle">
                            <thead>
                                <tr>
                                    <th>NAME</th>
                                    <th>STATUS</th>
                                    <th>CATEGORY</th>
                                    <th>LANGUAGE</th>
                                    <th>DESCRIPTION</th>
                                    <th className="text-center">ACTIONS</th>
                                </tr>
                            </thead>
                            <tbody>
                                {templates.map((tpl) => (
                                    <tr key={tpl.id}>
                                        <td>{tpl.name}</td>
                                        <td>{renderStatusBadge(tpl.status)}</td>
                                        <td>{CATEGORY_LABELS[tpl.category] || tpl.category}</td>
                                        <td>{tpl.language}</td>
                                        <td>{tpl.description}</td>
                                        <td className="text-center">
                                            <Button
                                                size="sm"
                                                variant="link"
                                                onClick={() => openViewModal(tpl)}
                                            >
                                                <GrView />
                                            </Button>
                                            <Button
                                                size="sm"
                                                variant="link"
                                                onClick={() => openEditModal(tpl)}
                                            >
                                                <FiEdit2 />
                                            </Button>
                                            <Button
                                                size="sm"
                                                variant="link"
                                                onClick={() => handleDelete(tpl)}
                                            >
                                                <RiDeleteBinLine />
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                                {!loading && templates.length === 0 && (
                                    <tr>
                                        <td colSpan={6} className="text-center text-muted py-4">
                                            No templates found.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </Table>
                    </div>

                    {loading && (
                        <div className="text-center text-muted py-2">Loading...</div>
                    )}

                    <div className="d-flex justify-content-between align-items-center mt-2">
                        <div className="text-muted small">
                            Page {page} of {totalPages}
                        </div>
                        <div className="d-flex gap-2">
                            <Button
                                variant="outline-secondary"
                                size="sm"
                                disabled={page <= 1}
                                onClick={() => setPage((p) => Math.max(1, p - 1))}
                            >
                                Previous
                            </Button>
                            <Button
                                variant="outline-secondary"
                                size="sm"
                                disabled={page >= totalPages}
                                onClick={() =>
                                    setPage((p) =>
                                        totalPages ? Math.min(totalPages, p + 1) : p + 1
                                    )
                                }
                            >
                                Next
                            </Button>
                        </div>
                    </div>
                </Card.Body>
            </Card>

            {/* Create / Edit Modal */}
            <Modal
                show={showEditModal}
                onHide={() => {
                    // Clean up preview URL when closing modal
                    if (currentTemplate?.headerImagePreview) {
                        URL.revokeObjectURL(currentTemplate.headerImagePreview);
                    }
                    setShowEditModal(false);
                }}
                size="lg"
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>
                        {editMode === "create" ? "Create Template" : "Edit Template"}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {currentTemplate && (
                        <Row>
                            <Col md={8}>
                                <Form>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Template Name*</Form.Label>
                                        <Form.Control
                                            value={currentTemplate.name}
                                            required
                                            onChange={(e) =>
                                                setCurrentTemplate((prev) => ({
                                                    ...prev,
                                                    name: e.target.value,
                                                }))
                                            }
                                            placeholder="e.g. welcome_message"
                                        />
                                    </Form.Group>

                                    <Row>
                                        <Col md={6}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>Category</Form.Label>
                                                <Form.Select
                                                    value={currentTemplate.category}
                                                    onChange={(e) =>
                                                        setCurrentTemplate((prev) => ({
                                                            ...prev,
                                                            category: Number(e.target.value),
                                                        }))
                                                    }
                                                >
                                                    {CATEGORY_OPTIONS.map((opt) => (
                                                        <option key={opt.value} value={opt.value}>
                                                            {opt.key.charAt(0) +
                                                                opt.key.slice(1).toLowerCase()}
                                                        </option>
                                                    ))}
                                                </Form.Select>
                                            </Form.Group>
                                        </Col>
                                        <Col md={6}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>Language</Form.Label>
                                                <Form.Select
                                                    value={currentTemplate.language}
                                                    onChange={(e) =>
                                                        setCurrentTemplate((prev) => ({
                                                            ...prev,
                                                            language: e.target.value,
                                                        }))
                                                    }
                                                >
                                                    {LANGUAGE_OPTIONS.map((opt) => (
                                                        <option key={opt} value={opt}>
                                                            {opt}
                                                        </option>
                                                    ))}
                                                </Form.Select>
                                            </Form.Group>
                                        </Col>
                                    </Row>

                                    <Form.Group className="mb-3">
                                        <Form.Label>Description*</Form.Label>
                                        <Form.Control
                                            value={currentTemplate.description}
                                            required
                                            onChange={(e) =>
                                                setCurrentTemplate((prev) => ({
                                                    ...prev,
                                                    description: e.target.value,
                                                }))
                                            }
                                            placeholder="Brief description of the template purpose"
                                        />
                                    </Form.Group>

                                    <Form.Group className="mb-3">
                                        <Form.Label>Header (optional)</Form.Label>
                                        <Form.Text className="d-block mb-2 text-muted" muted>
                                            Add a title or choose which type of media you'll use for
                                            this header.
                                        </Form.Text>
                                        <Form.Select
                                            value={currentTemplate.headerType}
                                            onChange={(e) => {
                                                const newType = Number(e.target.value);
                                                setCurrentTemplate((prev) => {
                                                    // Clean up preview URL if switching away from IMAGE
                                                    if (prev.headerType === WhatsAppTemplateHeaderType.IMAGE &&
                                                        newType !== WhatsAppTemplateHeaderType.IMAGE &&
                                                        prev.headerImagePreview) {
                                                        URL.revokeObjectURL(prev.headerImagePreview);
                                                    }
                                                    return {
                                                        ...prev,
                                                        headerType: newType,
                                                        // Clear image-related fields when switching to TEXT
                                                        ...(newType === WhatsAppTemplateHeaderType.TEXT && {
                                                            headerImageFile: null,
                                                            headerImageFileName: "",
                                                            headerImagePreview: null,
                                                        }),
                                                    };
                                                });
                                            }}
                                        >
                                            <option value={WhatsAppTemplateHeaderType.TEXT}>
                                                Text
                                            </option>
                                            <option value={WhatsAppTemplateHeaderType.IMAGE}>
                                                Image
                                            </option>
                                        </Form.Select>

                                        {currentTemplate.headerType ===
                                            WhatsAppTemplateHeaderType.TEXT && (
                                                <Form.Control
                                                    className="mt-2"
                                                    value={currentTemplate.header}
                                                    onChange={(e) =>
                                                        setCurrentTemplate((prev) => ({
                                                            ...prev,
                                                            header: e.target.value,
                                                        }))
                                                    }
                                                    placeholder="Add a title or header text"
                                                />
                                            )}

                                        {currentTemplate.headerType ===
                                            WhatsAppTemplateHeaderType.IMAGE && (
                                                <div className="mt-2">
                                                    <div className="d-flex flex-column gap-2">
                                                        <div className="d-flex gap-2">
                                                            <Form.Control
                                                                type="file"
                                                                accept="image/png,image/jpeg"
                                                                onChange={(e) => {
                                                                    const file = e.target.files?.[0];
                                                                    if (file) {
                                                                        // Validate file size (5 MB)
                                                                        if (file.size > 5 * 1024 * 1024) {
                                                                            alert("File size must be less than 5 MB");
                                                                            return;
                                                                        }
                                                                        // Create preview URL
                                                                        const previewUrl = URL.createObjectURL(file);
                                                                        setCurrentTemplate((prev) => {
                                                                            // Clean up previous preview URL if exists
                                                                            if (prev.headerImagePreview) {
                                                                                URL.revokeObjectURL(prev.headerImagePreview);
                                                                            }
                                                                            return {
                                                                                ...prev,
                                                                                headerImageFileName: file.name,
                                                                                headerImageFile: file,
                                                                                headerImagePreview: previewUrl,
                                                                            };
                                                                        });
                                                                    }
                                                                }}
                                                            />
                                                        </div>
                                                        <div className="text-muted small">
                                                            Accept only png &amp; jpeg and max size 5 MB
                                                        </div>

                                                        {/* Image Preview Area */}
                                                        {currentTemplate.headerImagePreview && (
                                                            <div className="mt-3">
                                                                <div
                                                                    className="border rounded bg-white"
                                                                    style={{
                                                                        width: "100%",
                                                                        minHeight: "300px",
                                                                        display: "flex",
                                                                        flexDirection: "column",
                                                                        alignItems: "center",
                                                                        justifyContent: "center",
                                                                        padding: "20px",
                                                                        position: "relative",
                                                                    }}
                                                                >
                                                                    <img
                                                                        src={currentTemplate.headerImagePreview}
                                                                        alt="Header preview"
                                                                        style={{
                                                                            maxWidth: "100%",
                                                                            maxHeight: "500px",
                                                                            height: "auto",
                                                                            borderRadius: "4px",
                                                                            objectFit: "contain",
                                                                        }}
                                                                    />
                                                                </div>
                                                                {currentTemplate.headerImageFileName && (
                                                                    <div className="mt-2 small text-success d-flex align-items-center gap-1">
                                                                        <span style={{ fontSize: "14px", fontWeight: "bold" }}>✓</span>
                                                                        <span className="text-truncate" style={{ fontSize: "13px" }}>
                                                                            {currentTemplate.headerImageFileName}
                                                                        </span>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                    </Form.Group>

                                    <Form.Group className="mb-3">
                                        <Form.Label>Body*</Form.Label>
                                        <Form.Control
                                            as="textarea"
                                            rows={4}
                                            value={currentTemplate.body}
                                            required
                                            onChange={(e) =>
                                                setCurrentTemplate((prev) => ({
                                                    ...prev,
                                                    body: e.target.value,
                                                }))
                                            }
                                            placeholder="Enter your message text. Use {{1}}, {{2}} for variables."
                                        />
                                        <Form.Text muted>
                                            Use {"{{1}}"}, {"{{2}}"} etc. for dynamic parameters.
                                        </Form.Text>
                                    </Form.Group>

                                    <Form.Group className="mb-3">
                                        <Form.Label>Footer (optional)</Form.Label>
                                        <Form.Control
                                            value={currentTemplate.footer}
                                            onChange={(e) =>
                                                setCurrentTemplate((prev) => ({
                                                    ...prev,
                                                    footer: e.target.value,
                                                }))
                                            }
                                            placeholder="Add footer text (e.g., opt-out message)"
                                        />
                                    </Form.Group>

                                    {/* Buttons Section */}
                                    <Form.Group className="mb-3">
                                        <Form.Label>Buttons</Form.Label>
                                        <Form.Text className="d-block mb-2 text-muted" muted>
                                            Create buttons that let customers respond to your message or
                                            take action.
                                        </Form.Text>
                                        <div className="mb-3">
                                            <Dropdown
                                                className="d-inline-block"
                                                align="start"
                                                drop="down"
                                            >
                                                <Dropdown.Toggle
                                                    variant="outline-secondary"
                                                    size="sm"
                                                >
                                                    + Add button
                                                </Dropdown.Toggle>
                                                <Dropdown.Menu>
                                                    <Dropdown.Item
                                                        onClick={() => addButton(BUTTON_TYPES.VISIT_WEBSITE)}
                                                    >
                                                        Visit website
                                                    </Dropdown.Item>
                                                    <Dropdown.Item
                                                        onClick={() => addButton(BUTTON_TYPES.CALL_WHATSAPP)}
                                                    >
                                                        Call on WhatsApp
                                                    </Dropdown.Item>
                                                </Dropdown.Menu>
                                            </Dropdown>
                                        </div>

                                        {(currentTemplate.buttons || []).map((btn) => (
                                            <Card key={btn.id} className="mb-2">
                                                <Card.Body>
                                                    <Row className="align-items-center g-2">
                                                        <Col md={3}>
                                                            <Form.Label className="mb-1 small">
                                                                Type of action
                                                            </Form.Label>
                                                            <Form.Select
                                                                size="sm"
                                                                value={btn.type}
                                                                onChange={(e) =>
                                                                    updateButton(btn.id, {
                                                                        type: e.target.value,
                                                                    })
                                                                }
                                                            >
                                                                <option value={BUTTON_TYPES.VISIT_WEBSITE}>
                                                                    Visit website
                                                                </option>
                                                                <option value={BUTTON_TYPES.CALL_WHATSAPP}>
                                                                    Call on WhatsApp
                                                                </option>
                                                            </Form.Select>
                                                        </Col>
                                                        <Col md={4}>
                                                            <Form.Label className="mb-1 small">
                                                                Button text
                                                            </Form.Label>
                                                            <Form.Control
                                                                size="sm"
                                                                value={btn.text}
                                                                maxLength={25}
                                                                onChange={(e) =>
                                                                    updateButton(btn.id, {
                                                                        text: e.target.value,
                                                                    })
                                                                }
                                                            />
                                                        </Col>

                                                        {btn.type === BUTTON_TYPES.VISIT_WEBSITE && (
                                                            <Col md={5}>
                                                                <Form.Label className="mb-1 small">
                                                                    Website URL
                                                                </Form.Label>
                                                                <Form.Control
                                                                    size="sm"
                                                                    placeholder="https://example.com"
                                                                    value={btn.url}
                                                                    onChange={(e) =>
                                                                        updateButton(btn.id, {
                                                                            url: e.target.value,
                                                                        })
                                                                    }
                                                                />
                                                            </Col>
                                                        )}

                                                        {btn.type === BUTTON_TYPES.CALL_WHATSAPP && (
                                                            <Col md={5}>
                                                                <Form.Label className="mb-1 small">
                                                                    Phone number
                                                                </Form.Label>
                                                                <Form.Control
                                                                    size="sm"
                                                                    placeholder="15550051310"
                                                                    value={btn.phone_number || ""}
                                                                    onChange={(e) =>
                                                                        updateButton(btn.id, {
                                                                            phone_number: e.target.value,
                                                                        })
                                                                    }
                                                                />
                                                            </Col>
                                                        )}
                                                    </Row>
                                                    <div className="text-end mt-2">
                                                        <Button
                                                            variant="link"
                                                            size="sm"
                                                            className="text-danger"
                                                            onClick={() => removeButton(btn.id)}
                                                        >
                                                            Remove
                                                        </Button>
                                                    </div>
                                                </Card.Body>
                                            </Card>
                                        ))}
                                    </Form.Group>
                                </Form>
                            </Col>

                            <Col md={4}>
                                <div className="border rounded p-3 bg-light">
                                    <div className="mb-2 text-muted small">Preview</div>
                                    <Card className="mb-3">
                                        <Card.Body>
                                            {/* Header Preview */}
                                            {currentTemplate.headerType === WhatsAppTemplateHeaderType.IMAGE && currentTemplate.headerImagePreview ? (
                                                <div className="mb-2">
                                                    <img
                                                        src={currentTemplate.headerImagePreview}
                                                        alt="Header preview"
                                                        style={{
                                                            maxWidth: "100%",
                                                            height: "auto",
                                                            borderRadius: "4px",
                                                        }}
                                                    />
                                                </div>
                                            ) : currentTemplate.headerType === WhatsAppTemplateHeaderType.TEXT && currentTemplate.header ? (
                                                <div className="text-muted small mb-2">
                                                    {currentTemplate.header}
                                                </div>
                                            ) : (
                                                <div className="text-muted small mb-2">
                                                    Your message will appear here...
                                                </div>
                                            )}
                                            {/* Body */}
                                            <div>{currentTemplate.body || ""}</div>
                                            {/* Footer */}
                                            {currentTemplate.footer && (
                                                <div className="mt-3 text-muted small">
                                                    {currentTemplate.footer}
                                                </div>
                                            )}
                                            {/* Buttons preview */}
                                            {(currentTemplate.buttons || []).length > 0 && (
                                                <div className="mt-3 d-flex flex-column gap-2">
                                                    {(currentTemplate.buttons || []).map((btn) => (

                                                        <Button
                                                            key={btn.id}
                                                            variant="light"
                                                            size="sm"
                                                            className="text-primary border d-inline-flex align-items-center justify-content-center flex-nowrap gap-2"
                                                            style={{ whiteSpace: "nowrap" }}
                                                        >
                                                            {btn.type === BUTTON_TYPES.CALL_WHATSAPP && (
                                                                <FaPhoneAlt size={14} className="flex-shrink-0" aria-hidden />
                                                            )}
                                                            {btn.type === BUTTON_TYPES.VISIT_WEBSITE && (
                                                                <FaExternalLinkAlt size={14} className="flex-shrink-0" aria-hidden />
                                                            )}
                                                            <span>{btn.text}</span>
                                                        </Button>
                                                    ))}
                                                </div>
                                            )}
                                        </Card.Body>
                                    </Card>
                                    <div className="alert alert-info mb-0 py-2 px-2 small">
                                        Variables like {`{{1}}`} will be replaced with actual values
                                        when sending.
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowEditModal(false)}>
                        Cancel
                    </Button>
                    {editMode == "create" && (
                        <Button variant="primary" onClick={handleSave}>
                            Submit for Approval
                        </Button>
                    )}
                    {editMode == "edit" && (
                        <Button variant="primary" onClick={handleEdit}>
                            Submit for Approval
                        </Button>
                    )}
                </Modal.Footer>
            </Modal>

            {/* View Modal */}
            <Modal
                show={showViewModal}
                onHide={() => setShowViewModal(false)}
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>
                        Template Preview: {viewTemplate?.name || ""}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {viewTemplate && (
                        <>
                            <div className="mb-2">
                                <strong>Status</strong>
                                <div>{renderStatusBadge(viewTemplate.status)}</div>
                            </div>
                            <div className="mb-2">
                                <strong>Category</strong>
                                <div>{renderCategoryLabel(viewTemplate.category)}</div>
                            </div>
                            <div className="mb-2">
                                <strong>Body</strong>
                                <Card className="mt-1">
                                    <Card.Body>{viewTemplate.body}</Card.Body>
                                </Card>
                            </div>
                            {viewTemplate.footer && (
                                <div className="mb-2">
                                    <strong>Footer</strong>
                                    <Card className="mt-1">
                                        <Card.Body>{viewTemplate.footer}</Card.Body>
                                    </Card>
                                </div>
                            )}
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowViewModal(false)}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default WhatsAppTemplate;